/* =========================================================
   Corals Anonymous - Bird Pickup Delivery Checkout Overlay V6

   Purpose:
   - Main cart drawer checkout CTA opens Bird delivery modal.
   - Continue button is blocked until an actual delivery/pickup date is detected.
   - Continue button still uses the native Shopify checkout button after validation,
     so BirdChime/cart integrations still get a chance to run.
   - Blocks the delivery modal from opening until product subtotal is at least $100.
   - Does NOT touch cart drawer footer, badge, insurance, or quantity logic.
   ========================================================= */

(function () {
  if (window.CA_DELIVERY_OVERLAY_INSTALLED) return;
  window.CA_DELIVERY_OVERLAY_INSTALLED = true;
  const OVERLAY_SELECTOR = '#MinimogCartDrawer [data-ca-bird-overlay]';
  const CHECKOUT_SELECTOR = '#MinimogCartDrawer [data-ca-checkout-cta]';
  const CLOSE_SELECTOR = '#MinimogCartDrawer [data-ca-bird-close]';
  const CONTINUE_SELECTOR = '#MinimogCartDrawer [data-ca-bird-continue]';
  const MOUNT_SELECTOR = '#MinimogCartDrawer [data-ca-bird-delivery-mount]';
  const OOS_PREFERENCE_SELECTOR = '#MinimogCartDrawer [data-ca-oos-preference]';
  const OOS_OPTION_SELECTOR = '#MinimogCartDrawer input[name="attributes[Out of Stock Preference]"]';
  const OOS_ERROR_SELECTOR = '#MinimogCartDrawer [data-ca-oos-error]';
  const OOS_ATTRIBUTE_NAME = 'Out of Stock Preference';

  const SPECIAL_INSTRUCTIONS_SELECTOR =
    '#MinimogCartDrawer [data-ca-special-instructions-input], #MinimogCartDrawer [data-ca-special-instructions] textarea, #MinimogCartDrawer [data-ca-bird-overlay] textarea[name="note"]';

  const ERROR_CLASS = 'ca-bird-delivery-error';
  const MINIMUM_ORDER_ERROR_CLASS = 'ca-minimum-order-error';
  const DISABLED_CLASS = 'is-disabled';

  const MIN_DELIVERY_MODAL_SUBTOTAL = 10000; // $100.00 product subtotal minimum

  const DATE_VALUE_REGEX =
    /(\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\.?\s+\d{1,2}(?:st|nd|rd|th)?(?:,\s*\d{4})?\b)|(\b\d{4}-\d{1,2}-\d{1,2}\b)|(\b\d{1,2}\/\d{1,2}(?:\/\d{2,4})?\b)/i;

  const DATE_FIELD_HINT_REGEX =
    /(date|calendar|day|delivery[-_\s]*date|pickup[-_\s]*date|shipping[-_\s]*date|delivery_day|pickup_day)/i;

  let collapseTimer = null;
  let errorTimer = null;
  let stateTimer = null;
  let mountObserver = null;
  let observedMount = null;
  let oosSavePromise = null;
  let lastSavedOosPreference = null;
  let noteSavePromise = null;
  let lastSavedCartNote = null;

  function getOverlay() {
    return document.querySelector(OVERLAY_SELECTOR);
  }

  function getMount() {
    return document.querySelector(MOUNT_SELECTOR);
  }

  function getContinueButton() {
    return document.querySelector(CONTINUE_SELECTOR);
  }

  function getNativeCheckoutButton() {
    const drawer = document.querySelector('#MinimogCartDrawer');

    if (!drawer) return null;

    return (
      drawer.querySelector('[data-ca-native-checkout]') ||
      drawer.querySelector('button[name="checkout"]:not([data-ca-checkout-cta])') ||
      drawer.querySelector('button[name="checkout"]')
    );
  }

  function getOosPreferenceBlock() {
    return document.querySelector(OOS_PREFERENCE_SELECTOR);
  }

  function getSelectedOosPreference() {
    const selected = document.querySelector(OOS_OPTION_SELECTOR + ':checked');

    return selected ? normalizeString(selected.value) : '';
  }

  function setOosSaving(isSaving) {
    const block = getOosPreferenceBlock();

    if (!block) return;

    block.classList.toggle('is-saving', Boolean(isSaving));
  }

  function clearOosError() {
    const block = getOosPreferenceBlock();
    const error = document.querySelector(OOS_ERROR_SELECTOR);

    if (block) {
      block.classList.remove('is-error');
    }

    if (error) {
      error.classList.remove('is-active');
    }
  }

  function showOosError(message) {
    const block = getOosPreferenceBlock();
    const error = document.querySelector(OOS_ERROR_SELECTOR);

    if (block) {
      block.classList.add('is-error');

      /*
        Do not use scrollIntoView here.
        The OOS selector now lives inside the delivery modal, and scrolling inside
        a fixed cart drawer can expose the cart note addon panel behind the footer.
      */
      if (typeof block.animate === 'function') {
        block.animate(
          [
            { transform: 'translateY(0)' },
            { transform: 'translateY(-2px)' },
            { transform: 'translateY(0)' }
          ],
          {
            duration: 420,
            easing: 'ease'
          }
        );
      }
    }

    if (error) {
      error.textContent = message || 'Please select an out-of-stock preference before checkout.';
      error.classList.add('is-active');
    }
  }

  async function saveOosPreference(value, force) {
    const normalizedValue = normalizeString(value);

    if (!normalizedValue) {
      return false;
    }

    if (!force && lastSavedOosPreference === normalizedValue) {
      return true;
    }

    if (oosSavePromise) {
      try {
        await oosSavePromise;
      } catch (error) {}
    }

    setOosSaving(true);

    oosSavePromise = fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        attributes: {
          [OOS_ATTRIBUTE_NAME]: normalizedValue
        }
      })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().catch(function () {
            return {};
          }).then(function (data) {
            throw new Error(data.description || data.message || 'Could not save out-of-stock preference.');
          });
        }

        return response.json();
      })
      .then(function () {
        lastSavedOosPreference = normalizedValue;
        return true;
      })
      .finally(function () {
        setOosSaving(false);
        oosSavePromise = null;
      });

    return oosSavePromise;
  }

  async function ensureOosPreferenceSaved() {
    const selectedValue = getSelectedOosPreference();

    if (!selectedValue) {
      showOosError('Please select an out-of-stock preference before checkout.');
      return false;
    }

    clearOosError();

    try {
      await saveOosPreference(selectedValue);
      return true;
    } catch (error) {
      showOosError(error.message || 'Could not save your out-of-stock preference. Please try again.');
      return false;
    }
  }

  function getSpecialInstructionsField() {
    return document.querySelector(SPECIAL_INSTRUCTIONS_SELECTOR);
  }

  function getSpecialInstructionsValue() {
    const field = getSpecialInstructionsField();

    return field ? String(field.value || '') : '';
  }

  function setSpecialInstructionsSaving(isSaving) {
    const field = getSpecialInstructionsField();

    if (!field) return;

    const block =
      field.closest('[data-ca-special-instructions]') ||
      field.closest('.ca-special-instructions');

    field.classList.toggle('is-saving', Boolean(isSaving));

    if (block) {
      block.classList.toggle('is-saving', Boolean(isSaving));
    }
  }

  async function saveSpecialInstructions(force) {
    const noteValue = getSpecialInstructionsValue();

    if (!force && lastSavedCartNote === noteValue) {
      return true;
    }

    if (noteSavePromise) {
      try {
        await noteSavePromise;
      } catch (error) {}
    }

    setSpecialInstructionsSaving(true);

    noteSavePromise = fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        note: noteValue
      })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().catch(function () {
            return {};
          }).then(function (data) {
            throw new Error(data.description || data.message || 'Could not save special instructions.');
          });
        }

        return response.json();
      })
      .then(function () {
        lastSavedCartNote = noteValue;
        return true;
      })
      .finally(function () {
        setSpecialInstructionsSaving(false);
        noteSavePromise = null;
      });

    return noteSavePromise;
  }

  function normalizeString(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function formatCartMoney(cents) {
    return '$' + (Math.max(0, Number(cents) || 0) / 100).toFixed(2);
  }

  function isNonProductFeeItem(item) {
    const title = String(item.product_title || item.title || '').toLowerCase();
    const handle = String(item.handle || '').toLowerCase();
    const vendor = String(item.vendor || '').toLowerCase();
    const sku = String(item.sku || '').toLowerCase();

    return (
      title.includes('shipping box') ||
      title.includes('box charge') ||
      title.includes('shipping protection') ||
      title.includes('insurance') ||
      handle.includes('shipping-box') ||
      handle.includes('box-charge') ||
      handle.includes('shipping-protection') ||
      vendor.includes('simplyinsurance') ||
      vendor.includes('simply insurance') ||
      sku.includes('shipping-protection') ||
      sku.includes('box')
    );
  }

  function getDrawerQualifyingSubtotalCents() {
    const progress = document.querySelector('#MinimogCartDrawer [data-ca-free-shipping-progress]');
    const rawSubtotal = progress ? progress.getAttribute('data-ca-free-shipping-qualifying-total') : '';

    return Number.parseInt(rawSubtotal || '0', 10) || 0;
  }

  async function getCurrentQualifyingSubtotalCents() {
    const fallbackSubtotal = getDrawerQualifyingSubtotalCents();
    const controller = new AbortController();
    const timeout = window.setTimeout(function () { controller.abort(); }, 8000);

    try {
      const response = await fetch('/cart.js', {
        headers: {
          Accept: 'application/json'
        },
        cache: 'no-store',
        signal: controller.signal
      });

      if (!response.ok) return fallbackSubtotal;

      const cart = await response.json();

      return (cart.items || []).reduce(function (total, item) {
        if (isNonProductFeeItem(item)) return total;

        return total + Number(item.final_line_price || 0);
      }, 0);
    } catch (error) {
      return fallbackSubtotal;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function getOrCreateMinimumOrderError() {
    const checkoutArea = document.querySelector('#MinimogCartDrawer [data-ca-checkout-area]');
    const footer = document.querySelector('#MinimogCartDrawerFooter');

    const target = checkoutArea || footer;

    if (!target) return null;

    let error = target.querySelector('.' + MINIMUM_ORDER_ERROR_CLASS);

    if (error) return error;

    error = document.createElement('div');
    error.className = MINIMUM_ORDER_ERROR_CLASS;
    error.setAttribute('role', 'alert');
    error.setAttribute('aria-live', 'polite');

    target.prepend(error);

    return error;
  }

  function showMinimumOrderError(subtotalCents) {
    const error = getOrCreateMinimumOrderError();

    if (!error) return;

    const remaining = MIN_DELIVERY_MODAL_SUBTOTAL - subtotalCents;

    error.textContent =
      'Minimum product subtotal for checkout is ' +
      formatCartMoney(MIN_DELIVERY_MODAL_SUBTOTAL);

    error.classList.add('is-active');
  }

  function clearMinimumOrderError() {
    const error = document.querySelector('#MinimogCartDrawer .' + MINIMUM_ORDER_ERROR_CLASS);

    if (!error) return;

    error.classList.remove('is-active');
    error.textContent = '';
  }

  async function canOpenDeliveryModal() {
    const subtotalCents = await getCurrentQualifyingSubtotalCents();

    if (subtotalCents >= MIN_DELIVERY_MODAL_SUBTOTAL) {
      clearMinimumOrderError();
      return true;
    }

    showMinimumOrderError(subtotalCents);
    return false;
  }

  function looksLikeDateValue(value) {
    const text = normalizeString(value);

    if (!text) return false;

    const lower = text.toLowerCase();

    if (
      lower === 'shipping' ||
      lower === 'pickup' ||
      lower === 'local pickup' ||
      lower === 'delivery' ||
      lower === 'true' ||
      lower === 'false' ||
      lower === 'on'
    ) {
      return false;
    }

    return DATE_VALUE_REGEX.test(text);
  }

  function getElementMeta(element) {
    if (!element) return '';

    return normalizeString(
      [
        element.name,
        element.id,
        element.className,
        element.getAttribute && element.getAttribute('aria-label'),
        element.getAttribute && element.getAttribute('placeholder'),
        element.getAttribute && element.getAttribute('title'),
        element.getAttribute && element.getAttribute('data-date'),
        element.getAttribute && element.getAttribute('data-day'),
        element.getAttribute && element.getAttribute('data-value'),
        element.getAttribute && element.getAttribute('data-selected-date')
      ].join(' ')
    );
  }

  function hasDateFromFields(root) {
    const fields = root.querySelectorAll('input, select, textarea');

    for (const field of fields) {
      if (field.disabled) continue;

      const type = normalizeString(field.type).toLowerCase();
      const value = normalizeString(field.value);
      const meta = getElementMeta(field);

      if ((type === 'radio' || type === 'checkbox') && !field.checked) {
        continue;
      }

      if (type === 'date' && value) {
        return true;
      }

      if (looksLikeDateValue(value)) {
        return true;
      }

      /*
        This catches cases where BirdChime stores the selected date in a hidden
        input with a clear date-related name, but maybe not a human-readable value.
        We intentionally require a date-ish field name so "shipping" / "pickup"
        method fields don't accidentally count as a selected date.
      */
      if (DATE_FIELD_HINT_REGEX.test(meta) && looksLikeDateValue(value)) {
        return true;
      }
    }

    return false;
  }

  function hasDateFromSelectedElements(root) {
    const selectedElements = root.querySelectorAll(
      [
        '[aria-selected="true"]',
        '[aria-checked="true"]',
        '[data-selected="true"]',
        '[data-selected-date]',
        '.selected',
        '.is-selected',
        '.active'
      ].join(',')
    );

    for (const element of selectedElements) {
      const possibleValues = [
        element.getAttribute('data-date'),
        element.getAttribute('data-selected-date'),
        element.getAttribute('data-day'),
        element.getAttribute('data-value'),
        element.getAttribute('aria-label'),
        element.getAttribute('title'),
        element.textContent
      ];

      if (possibleValues.some(looksLikeDateValue)) {
        return true;
      }
    }

    return false;
  }

  function hasSelectedDeliveryDate() {
    const mount = getMount();

    if (!mount) return false;

    const dateField = mount.querySelector('#birdchime-slots-box .bcdp-widget-input');
    return Boolean(dateField && !dateField.disabled && looksLikeDateValue(dateField.value));
  }

  function hasBirdWidgetRendered() {
    const mount = getMount();

    if (!mount) return false;

    const field = mount.querySelector('#birdchime-slots-box .bcdp-widget-input');
    if (!field || field.disabled || field.closest('[hidden], .birdchime-slots-box-hide')) return false;
    for (let node = field; node && node !== mount; node = node.parentElement) {
      const style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
    }
    return true;
  }

  function setContinueState(isReady) {
    const continueButton = getContinueButton();

    if (!continueButton) return;

    continueButton.classList.toggle(DISABLED_CLASS, !isReady);
    continueButton.setAttribute('aria-disabled', isReady ? 'false' : 'true');

    /*
      Do not apply the real disabled attribute here.
      Keeping the button clickable lets us show a clear error message instead
      of making the customer think the button is broken.
    */
    if (continueButton.hasAttribute('disabled')) {
      continueButton.removeAttribute('disabled');
    }

    if (continueButton.tagName.toLowerCase() === 'a') {
      continueButton.setAttribute('role', 'button');
    }
  }

  function syncContinueState() {
    setContinueState(hasBirdWidgetRendered() && hasSelectedDeliveryDate());
  }

  function scheduleStateSync(delay) {
    window.clearTimeout(stateTimer);

    stateTimer = window.setTimeout(function () {
      syncContinueState();
    }, delay || 50);
  }

  function observeMount() {
    const mount = getMount();

    if (!mount || mount === observedMount) return;

    if (mountObserver) {
      mountObserver.disconnect();
    }

    observedMount = mount;

    mountObserver = new MutationObserver(function () {
      reconcileWidget();
      scheduleStateSync(50);
    });

    mountObserver.observe(mount, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
      attributeFilter: [
        'class',
        'value',
        'checked',
        'aria-selected',
        'aria-checked',
        'data-selected',
        'data-date',
        'data-day',
        'data-value',
        'data-selected-date'
      ]
    });

    syncContinueState();
  }


  // Bird's first initialization can precede AJAX cart markup. Reconcile when the
  // modal opens, when its mount changes, and when its asynchronous script arrives.
  // Never clone a React tree or clear a populated widget: both lose live handlers.
  let widgetTimer = null;
  let widgetDeadline = 0;
  let lastWidgetInit = 0;
  let lifecycleQueued = false;
  let recoveryObserver = null;
  let widgetWasReady = false;

  function isOverlayOpen() {
    return Boolean(getOverlay()?.classList.contains('ca-bird-delivery-overlay--active'));
  }

  function getWidgetStatus() {
    const mount = getMount();
    if (!mount) return null;
    let status = mount.parentElement.querySelector('[data-ca-delivery-status]');
    if (!status) {
      status = document.createElement('div');
      status.setAttribute('data-ca-delivery-status', '');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.style.cssText = 'padding:14px;margin:12px 0;border:1px solid #e5e7eb;border-radius:10px;background:#f9fafb;color:#374151;font-size:14px;line-height:1.5';
      const message = document.createElement('p');
      message.setAttribute('data-ca-delivery-status-message', '');
      message.style.margin = '0';
      const refresh = document.createElement('button');
      refresh.type = 'button';
      refresh.setAttribute('data-ca-delivery-refresh', '');
      refresh.textContent = 'Reload delivery dates';
      refresh.style.cssText = 'margin-top:10px;padding:8px 12px;border:1px solid #9ca3af;border-radius:6px;background:white;color:#111827;font-weight:600';
      refresh.addEventListener('click', function () { window.location.reload(); });
      status.append(message, refresh);
      mount.insertAdjacentElement('afterend', status);
    }
    return status;
  }

  function reconcileWidget() {
    const overlay = getOverlay();
    // An in-flight cart render can replace the open modal with fresh closed markup.
    if (overlay && document.body.classList.contains('ca-bird-overlay-open') && !isOverlayOpen()) {
      overlay.classList.add('ca-bird-delivery-overlay--active');
      overlay.setAttribute('aria-hidden', 'false');
      widgetDeadline = Date.now() + 20000;
    }
    if (!isOverlayOpen()) return;
    const mount = getMount();
    if (!mount) return;
    // Recover a widget injected next to a checkout control instead of its mount.
    const root = document.getElementById('birdchime-slots-box');
    if (root && !mount.contains(root) && !mount.querySelector('#birdchime-slots-box')) {
      mount.appendChild(root);
    }
    observeMount();
    if (widgetWasReady && !hasBirdWidgetRendered()) {
      widgetDeadline = Date.now() + 20000;
      lastWidgetInit = 0;
    }
    widgetWasReady = hasBirdWidgetRendered();
    if (!hasBirdWidgetRendered() && Date.now() < widgetDeadline &&
        Date.now() - lastWidgetInit >= 1500 && typeof window.BirdChimeDateWidget === 'function') {
      const currentRoot = mount.querySelector('#birdchime-slots-box');
      // Bird mounts only empty roots; do not repeatedly register its observers
      // while an existing component is fetching its availability.
      if (!currentRoot || currentRoot.children.length === 0) {
        lastWidgetInit = Date.now();
        try { window.BirdChimeDateWidget(); } catch (error) {
          console.warn('[Delivery dates] Widget initialization failed.', error);
        }
      }
    }
    const ready = hasBirdWidgetRendered();
    widgetWasReady = ready;
    const status = getWidgetStatus();
    const expired = Date.now() >= widgetDeadline;
    if (status) {
      if (status.hidden !== ready) status.hidden = ready;
      const message = ready ? '' : expired
        ? 'Delivery dates could not load. Please reload to try again. Your cart items will be kept.'
        : 'Loading available delivery and pickup dates…';
      const paragraph = status.querySelector('[data-ca-delivery-status-message]');
      if (paragraph.textContent !== message) paragraph.textContent = message;
      const refresh = status.querySelector('[data-ca-delivery-refresh]');
      if (refresh.hidden !== !expired) refresh.hidden = !expired;
    }
    if (mount.getAttribute('aria-busy') !== String(!ready)) mount.setAttribute('aria-busy', String(!ready));
    syncContinueState();
    if (ready || expired) {
      window.clearTimeout(widgetTimer);
      widgetTimer = null;
    } else if (widgetTimer === null) {
      widgetTimer = window.setTimeout(function () {
        widgetTimer = null;
        reconcileWidget();
      }, 500);
    }
  }

  function startWidgetRecovery() {
    stopWidgetRecovery();
    widgetDeadline = Date.now() + 20000;
    lastWidgetInit = 0;
    reconcileWidget();
  }

  function stopWidgetRecovery() {
    window.clearTimeout(widgetTimer);
    widgetTimer = null;
  }

  function installWidgetLifecycle() {
    if (recoveryObserver) return;
    recoveryObserver = new MutationObserver(function (records) {
      // Ignore unrelated recommendations, countdowns and other page mutations.
      const relevant = records.some(function (record) {
        return record.target.closest?.('#MinimogCartDrawer') ||
          Array.from(record.addedNodes).some(function (node) {
            return node.nodeType === 1 && (node.matches?.('#MinimogCartDrawer, #birdchime-slots-box') ||
              node.querySelector?.('#MinimogCartDrawer, #birdchime-slots-box'));
          });
      });
      if (!relevant || lifecycleQueued) return;
      lifecycleQueued = true;
      window.setTimeout(function () {
        lifecycleQueued = false;
        observeMount();
        reconcileWidget();
      }, 50);
    });
    recoveryObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('online', function () {
      if (isOverlayOpen() && !hasBirdWidgetRendered()) startWidgetRecovery();
    });
    window.addEventListener('pageshow', function () {
      if (isOverlayOpen()) startWidgetRecovery();
    });
    document.addEventListener('load', function (event) {
      if (event.target.tagName === 'SCRIPT' && /birdchime|bird-pickup/.test(event.target.src || '')) {
        if (isOverlayOpen() && !hasBirdWidgetRendered()) startWidgetRecovery();
        else reconcileWidget();
      }
    }, true);
  }

  function installStyles() {
    if (document.getElementById('ca-bird-overlay-guard-styles')) return;

    const style = document.createElement('style');

    style.id = 'ca-bird-overlay-guard-styles';
    style.textContent = `
      html.ca-bird-overlay-open,
      body.ca-bird-overlay-open {
        overflow: hidden !important;
        overscroll-behavior: none !important;
      }

      html.ca-bird-overlay-open #MinimogCartDrawer,
      body.ca-bird-overlay-open #MinimogCartDrawer {
        position: fixed !important;
        inset: 0 !important;
        z-index: 2147483600 !important;
        isolation: isolate !important;
      }

      html.ca-bird-overlay-open #MinimogCartDrawer .m-cart-drawer__inner,
      body.ca-bird-overlay-open #MinimogCartDrawer .m-cart-drawer__inner {
        position: relative !important;
        z-index: 2147483601 !important;
      }

      html.ca-bird-overlay-open #MinimogCartDrawer .ca-bird-delivery-overlay,
      body.ca-bird-overlay-open #MinimogCartDrawer .ca-bird-delivery-overlay {
        z-index: 2147483602 !important;
      }
      #MinimogCartDrawer
        .ca-bird-delivery-overlay.ca-bird-calendar-expanded
        .ca-bird-delivery-modal {
        overflow: visible !important;
      }

      #MinimogCartDrawer
        .ca-bird-delivery-overlay.ca-bird-calendar-expanded
        .ca-bird-delivery-mount {
        position: relative !important;
        z-index: 50 !important;
        overflow: visible !important;
      }

      #MinimogCartDrawer
        .ca-bird-delivery-overlay.ca-bird-calendar-expanded
        .ca-oos-preference--modal,
      #MinimogCartDrawer
        .ca-bird-delivery-overlay.ca-bird-calendar-expanded
        .ca-bird-delivery-actions {
        position: relative !important;
        z-index: 1 !important;
      }
      #MinimogCartDrawer .${ERROR_CLASS} {
        display: none;
        margin: 12px 0 0;
        padding: 10px 12px;
        border-radius: 10px;
        background: rgba(230, 60, 47, 0.1);
        color: #9f1f16;
        font-size: 13px;
        font-weight: 700;
        line-height: 1.35;
      }

      #MinimogCartDrawer .${ERROR_CLASS}.is-active {
        display: block;
      }

      #MinimogCartDrawer .${MINIMUM_ORDER_ERROR_CLASS} {
        display: none;
        width: 100%;
        margin: 0 0 10px;
        padding: 10px 12px;
        border: 1px solid rgba(163, 45, 45, 0.18);
        border-radius: 12px;
        background: rgba(230, 60, 47, 0.1);
        color: #9f1f16;
        font-size: 12px;
        font-weight: 800;
        line-height: 1.35;
        text-align: center;
      }

      #MinimogCartDrawer .${MINIMUM_ORDER_ERROR_CLASS}.is-active {
        display: block;
      }

      #MinimogCartDrawer .ca-bird-delivery-continue {
        pointer-events: auto !important;
      }

      #MinimogCartDrawer button.ca-bird-delivery-continue {
        border: 0;
        font: inherit;
        text-decoration: none;
      }

      #MinimogCartDrawer .ca-bird-delivery-continue.${DISABLED_CLASS} {
        opacity: 0.55;
        cursor: not-allowed;
      }
    `;

    document.head.appendChild(style);
  }

  function getOrCreateError() {
    const overlay = getOverlay();
    const mount = getMount();

    if (!overlay) return null;

    let error = overlay.querySelector('.' + ERROR_CLASS);

    if (error) return error;

    error = document.createElement('div');
    error.className = ERROR_CLASS;
    error.setAttribute('role', 'alert');
    error.setAttribute('aria-live', 'polite');
    error.textContent = 'Please select a delivery or pickup date before continuing to checkout.';

    if (mount) {
      mount.insertAdjacentElement('afterend', error);
      return error;
    }

    const actions = overlay.querySelector('.ca-bird-delivery-actions');

    if (actions) {
      actions.insertAdjacentElement('beforebegin', error);
      return error;
    }

    overlay.appendChild(error);

    return error;
  }

  function showError(message) {
    const error = getOrCreateError();

    if (!error) return;

    window.clearTimeout(errorTimer);

    error.textContent = message || 'Please select a delivery or pickup date before continuing to checkout.';
    error.classList.add('is-active');

    errorTimer = window.setTimeout(function () {
      error.classList.remove('is-active');
    }, 5000);
  }

  function clearError() {
    const overlay = getOverlay();

    if (!overlay) return;

    const error = overlay.querySelector('.' + ERROR_CLASS);

    if (error) {
      error.classList.remove('is-active');
    }
  }

  function expandModal() {
    const overlay = getOverlay();

    if (!overlay) return;

    window.clearTimeout(collapseTimer);
    overlay.classList.add('ca-bird-calendar-expanded');
  }

  function collapseModal() {
    const overlay = getOverlay();

    if (!overlay) return;

    window.clearTimeout(collapseTimer);
    overlay.classList.remove('ca-bird-calendar-expanded');
  }

  function scheduleCollapseModal(delay) {
    window.clearTimeout(collapseTimer);

    collapseTimer = window.setTimeout(function () {
      collapseModal();
    }, delay || 250);
  }

  function openOverlay() {
    const overlay = getOverlay();

    if (!overlay) {
      showError('Delivery selector is not ready yet. Please try again in a moment.');
      return;
    }

    overlay.classList.add('ca-bird-delivery-overlay--active');
    overlay.setAttribute('aria-hidden', 'false');

    document.documentElement.classList.add('ca-bird-overlay-open');
    document.body.classList.add('ca-bird-overlay-open');

    clearMinimumOrderError();
    clearError();
    clearOosError();
    observeMount();
    startWidgetRecovery();

    scheduleStateSync(0);
    scheduleStateSync(150);
    scheduleStateSync(600);

    window.setTimeout(function () {
      const firstFocusable = overlay.querySelector(
        'button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (firstFocusable) firstFocusable.focus();
    }, 80);
  }

  function closeOverlay() {
    stopWidgetRecovery();
    const overlay = getOverlay();

    if (!overlay) return;

    collapseModal();
    clearError();
    clearOosError();

    overlay.classList.remove('ca-bird-delivery-overlay--active');
    overlay.setAttribute('aria-hidden', 'true');

    document.documentElement.classList.remove('ca-bird-overlay-open');
    document.body.classList.remove('ca-bird-overlay-open');
  }

  function clickNativeCheckout(nativeCheckout) {
    /*
      BirdChime or another script may have incorrectly disabled the hidden
      native checkout button. Since this overlay now validates the selected date
      itself, temporarily allow the native click so existing checkout listeners
      can still run.
    */
    const hadDisabledAttribute = nativeCheckout.hasAttribute('disabled');
    const wasDisabledProperty = nativeCheckout.disabled;
    const previousAriaDisabled = nativeCheckout.getAttribute('aria-disabled');

    if (hadDisabledAttribute || wasDisabledProperty) {
      nativeCheckout.disabled = false;
      nativeCheckout.removeAttribute('disabled');
      nativeCheckout.setAttribute('aria-disabled', 'false');
    }

    window.location.href = '/checkout';

    window.setTimeout(function () {
      if (!document.body.contains(nativeCheckout)) return;

      if (hadDisabledAttribute || wasDisabledProperty) {
        nativeCheckout.disabled = true;
        nativeCheckout.setAttribute('disabled', 'disabled');
      }

      if (previousAriaDisabled === null) {
        nativeCheckout.removeAttribute('aria-disabled');
      } else {
        nativeCheckout.setAttribute('aria-disabled', previousAriaDisabled);
      }
    }, 1000);
  }

  async function continueThroughNativeCheckout() {
    const nativeCheckout = getNativeCheckoutButton();

    observeMount();
    syncContinueState();

    if (!hasBirdWidgetRendered()) {
      expandModal();
      showError('The delivery date selector is still loading. Please wait a moment, then choose a date.');
      return;
    }

    if (!hasSelectedDeliveryDate()) {
      expandModal();
      setContinueState(false);
      showError('Please select a delivery or pickup date before continuing to checkout.');
      return;
    }

    const oosPreferenceIsReady = await ensureOosPreferenceSaved();

    if (!oosPreferenceIsReady) {
      expandModal();
      return;
    }

    try {
      await saveSpecialInstructions();
    } catch (error) {
      expandModal();
      showError(error.message || 'Could not save your special instructions. Please try again.');
      return;
    }

    if (!nativeCheckout) {
      expandModal();
      showError('Checkout is not ready yet. Please try again in a moment.');
      return;
    }

    try {
      if (!window.CAShippingBoxGuard) throw new Error('The shipping box check is still loading. Please refresh and try again.');
      await window.CAShippingBoxGuard.ensure();
    } catch (error) {
      expandModal();
      showError(error.message || 'Could not verify the shipping box quantity. Please try again.');
      return;
    }

    clearError();
    clearOosError();
    setContinueState(true);
    clickNativeCheckout(nativeCheckout);

    window.setTimeout(function () {
      expandModal();
    }, 250);
  }

  /*
    Main cart drawer checkout button:
    Open the Bird delivery modal first, but only after the cart reaches the
    minimum product subtotal. Required checkout preferences are validated when
    the customer clicks Continue inside the modal.
  */
  document.addEventListener(
    'click',
    async function (event) {
      const checkout = event.target && event.target.closest
        ? event.target.closest(CHECKOUT_SELECTOR)
        : null;

      if (!checkout) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const canOpen = await canOpenDeliveryModal();

      if (!canOpen) return;

      openOverlay();
    },
    true
  );

  /*
    Back to cart / close overlay.

    Important:
    stopImmediatePropagation prevents the modal close click from also reaching
    the cart drawer's global close listener.
  */
  document.addEventListener(
    'click',
    function (event) {
      const closeButton = event.target && event.target.closest
        ? event.target.closest(CLOSE_SELECTOR)
        : null;

      if (!closeButton) return;

      const overlay = getOverlay();

      if (!overlay || !overlay.contains(closeButton)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      closeOverlay();
    },
    true
  );

  /*
    Continue to checkout:
    Blocked until a selected delivery date and out-of-stock preference are detected.
  */
  document.addEventListener(
    'click',
    async function (event) {
      const continueButton = event.target && event.target.closest
        ? event.target.closest(CONTINUE_SELECTOR)
        : null;

      if (!continueButton) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      await continueThroughNativeCheckout();
    },
    true
  );

  /*
    Out-of-stock preference:
    Save the customer's choice immediately so it is available on the order.
  */
  document.addEventListener(
    'change',
    function (event) {
      const option = event.target && event.target.closest
        ? event.target.closest(OOS_OPTION_SELECTOR)
        : null;

      if (!option) return;

      clearOosError();

      saveOosPreference(option.value).catch(function (error) {
        showOosError(error.message || 'Could not save your out-of-stock preference. Please try again.');
      });
    },
    true
  );

  /*
    Keep button state synced when the customer interacts with the Bird widget.
  */
  document.addEventListener(
    'focusin',
    function (event) {
      const mount = event.target && event.target.closest
        ? event.target.closest(MOUNT_SELECTOR)
        : null;

      if (!mount) return;

      clearError();
      expandModal();
      scheduleStateSync(50);
      scheduleStateSync(250);
    },
    true
  );

  document.addEventListener(
    'click',
    function (event) {
      const mount = event.target && event.target.closest
        ? event.target.closest(MOUNT_SELECTOR)
        : null;

      if (!mount) return;

      clearError();
      expandModal();
      scheduleStateSync(50);
      scheduleStateSync(250);
      scheduleStateSync(600);
    },
    true
  );

  document.addEventListener(
    'input',
    function (event) {
      const mount = event.target && event.target.closest
        ? event.target.closest(MOUNT_SELECTOR)
        : null;

      if (!mount) return;

      clearError();
      expandModal();
      scheduleStateSync(50);
      scheduleStateSync(250);
    },
    true
  );

  document.addEventListener(
    'change',
    function (event) {
      const mount = event.target && event.target.closest
        ? event.target.closest(MOUNT_SELECTOR)
        : null;

      if (!mount) return;

      clearError();
      expandModal();
      scheduleStateSync(50);
      scheduleStateSync(250);
      scheduleStateSync(600);
    },
    true
  );

  /*
    Shrink back after the customer clicks outside the Bird widget,
    but keep the modal open.
  */
  document.addEventListener(
    'click',
    function (event) {
      const overlay = getOverlay();

      if (!overlay || !overlay.classList.contains('ca-bird-delivery-overlay--active')) return;

      const clickedInsideMount =
        event.target && event.target.closest
          ? event.target.closest(MOUNT_SELECTOR)
          : null;

      const clickedContinue =
        event.target && event.target.closest
          ? event.target.closest(CONTINUE_SELECTOR)
          : null;

      const clickedClose =
        event.target && event.target.closest
          ? event.target.closest(CLOSE_SELECTOR)
          : null;

      const clickedInsideOos =
        event.target && event.target.closest
          ? event.target.closest(OOS_PREFERENCE_SELECTOR)
          : null;

      const clickedInsideSpecialInstructions =
        event.target && event.target.closest
          ? event.target.closest('[data-ca-special-instructions], .ca-special-instructions, textarea[name="note"]')
          : null;

      if (
        clickedInsideMount ||
        clickedInsideOos ||
        clickedInsideSpecialInstructions ||
        clickedContinue ||
        clickedClose
      ) return;

      scheduleCollapseModal(250);
    },
    true
  );

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;

    const overlay = getOverlay();

    if (!overlay || !overlay.classList.contains('ca-bird-delivery-overlay--active')) return;

    closeOverlay();
  });

  document.addEventListener(
    'click',
    function (event) {
      const overlay = getOverlay();

      if (!overlay || !overlay.classList.contains('ca-bird-delivery-overlay--active')) return;

      if (event.target === overlay) {
        closeOverlay();
      }
    },
    true
  );

  function init() {
    installStyles();
    installWidgetLifecycle();
    lastSavedCartNote = getSpecialInstructionsValue();

    const selectedOosPreference = getSelectedOosPreference();

    if (selectedOosPreference) {
      lastSavedOosPreference = selectedOosPreference;
    }

    observeMount();
    syncContinueState();

    window.setTimeout(function () {
      observeMount();
      syncContinueState();

      const refreshedOosPreference = getSelectedOosPreference();

      if (refreshedOosPreference) {
        lastSavedOosPreference = refreshedOosPreference;
      }

      lastSavedCartNote = getSpecialInstructionsValue();
    }, 800);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

